import { formatDurationToDayHHMM } from "@utils/utils";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useReviewCompletionTimeRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface ReviewCompletionTimeRawDataType {
  clientId: number;
  clientName: string;
  sessionDate: string;
  sessionDatetime: string;
  sessionRecordedBy: string;
  readyForReviewAt: string;
  reviewedAt: string;
  sessionViewedByBcba: string;
  reviewCompletionTimeInSeconds: number;
}
export const ReviewCompletionTimeRawData = () => {
  const { selectedDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedDates);
  const [selectedBCBA, setSelectedBCBA] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedBCBA, selectedClient]);

  const { data, isFetching } = useReviewCompletionTimeRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      bcbaProviderIds: selectedBCBA.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["videosReviewedRawData", dates, selectedBCBA, pageNo, pageSize, selectedClient],
    },
  );

  const columns: Column<ReviewCompletionTimeRawDataType>[] = [
    {
      title: "Client ID",
      key: "clientId",
      dataIndex: "clientId",
    },
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      render: (value) => <span className="ph-no-capture">{(value as string) || "-"}</span>,
    },
    {
      title: "Session Date & Time",
      key: "sessionDatetime",
      dataIndex: "sessionDatetime",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Review Start",
      key: "readyForReviewAt",
      dataIndex: "readyForReviewAt",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Review End",
      key: "reviewedAt",
      dataIndex: "reviewedAt",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Review Completion Time",
      key: "totalComments",
      dataIndex: "reviewCompletionTimeInSeconds",
      render: (value) => {
        return value ? formatDurationToDayHHMM(value as number) : "-";
      },
    },
    {
      title: "Session Recorder",
      key: "sessionRecordedBy",
      dataIndex: "sessionRecordedBy",
    },
  ];

  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedBCBA={selectedBCBA}
        setSelectedBCBA={setSelectedBCBA}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showRBTFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.reviewCompletionTimeRawData?.rawData
            ? (data.reviewCompletionTimeRawData?.rawData as ReviewCompletionTimeRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.reviewCompletionTimeRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
